import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, MessageSquare, Calendar, User } from 'lucide-react';
import { customerFeedbackService, CustomerFeedback } from '@/services/customerFeedbackService';
import { supabase } from '@/integrations/supabase/client';

interface CustomerFeedbackDisplayProps {
  venueId: string;
}

export const CustomerFeedbackDisplay = ({ venueId }: CustomerFeedbackDisplayProps) => {
  const [feedback, setFeedback] = useState<CustomerFeedback[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [customerNames, setCustomerNames] = useState<Record<string, string>>({});

  useEffect(() => {
    loadFeedback();
  }, [venueId]);

  const loadFeedback = async () => {
    try {
      const feedbackData = await customerFeedbackService.getVenueFeedback(venueId);
      setFeedback(feedbackData);

      // Load customer names
      if (feedbackData.length > 0) {
        const customerIds = [...new Set(feedbackData.map(f => f.customer_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, username')
          .in('id', customerIds);

        const names: Record<string, string> = {};
        profiles?.forEach(p => {
          names[p.id] = p.first_name && p.last_name 
            ? `${p.first_name} ${p.last_name}`
            : p.username || 'Anonymous';
        });
        
        // Handle any remaining customer IDs not found in profiles
        customerIds.forEach(id => {
          if (!names[id]) {
            names[id] = 'Anonymous Customer';
          }
        });

        setCustomerNames(names);
      }
    } catch (error) {
      console.error('Error loading feedback:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'text-green-400';
    if (rating >= 3) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getRatingBadgeColor = (rating: number) => {
    if (rating >= 4) return 'bg-green-900 text-green-300';
    if (rating >= 3) return 'bg-yellow-900 text-yellow-300';
    return 'bg-red-900 text-red-300';
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'fill-current text-yellow-400' : 'text-gray-600'
        }`}
      />
    ));
  };

  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-800 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-primary" />
          Customer Feedback
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {feedback.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p>No customer feedback yet.</p>
            <p className="text-sm">Feedback will appear here after customers rate their experience.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedback.map(item => (
              <div
                key={item.id}
                className="bg-gray-800 border border-gray-700 rounded-lg p-4"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      {renderStars(item.rating)}
                    </div>
                    <Badge className={getRatingBadgeColor(item.rating)}>
                      {item.rating}/5
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-400 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(item.visit_date).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">
                    {customerNames[item.customer_id] || 'Anonymous Customer'}
                  </span>
                </div>

                {item.feedback_text && (
                  <p className="text-gray-300 text-sm leading-relaxed">
                    "{item.feedback_text}"
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};